import { FC, useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { BaseGridData } from "../../../../dtos/base-grid-data";
import { useBaseTable } from "../../../../hooks/useBaseTable";
import { currencyFormat } from "../../../../utilities/currencyFunctions";
import { BaseTable2, Col, FontBold } from "../../../TrueUI";
import Row, { rowWithNoMarginNorGutter } from "../../../TrueUI/Grids/Row";
import {
  BaseTable2Properties,
  ComputeForCellParameters,
} from "../../../TrueUI/Tables/BaseTable2/TableProperties";
import { BindInstructionsInfoAtom } from "./PolicyQuoteBindInstructionsAtoms";
import {
  PolicyQuoteBindInstructionsTableProps,
  UpdateDueDateInvoiceData,
} from "./PolicyQuoteBindInstructionsTypes";
import {
  getCommonTablesProps,
  getCurrentTotalPremium,
  validateInvoicesDueDateOnChange,
} from "./PolicyQuoteBindInstructionsUtils";
import { GettingDateWithoutTime } from "../../../../utilities/dateFunctions";

const PolicyQuoteBindInstructionsTable: FC<
  PolicyQuoteBindInstructionsTableProps
> = ({ insuredId, readOnly, setIsDialogOpen }) => {
  const TABLE_NAME = `policy_payment_schedule_table_${insuredId}`;
  const [bindInstructionsInfo, setBindInstructionsInfo] = useRecoilState(
    BindInstructionsInfoAtom
  );
  const [updateInvoiceData, setUpdateInvoiceData] =
    useState<UpdateDueDateInvoiceData | null>(null);
  const scheduleTableInfo = bindInstructionsInfo?.scheduleTableInfo;

  const CustomTableFooterComponent = (data?: string[][] | BaseGridData[]) => {
    if ((data?.length ?? 0) <= 0) {
      return <></>;
    }

    const isNonEarning = bindInstructionsInfo?.nonEarningAmount ?? false;

    const totalPremium = getCurrentTotalPremium(bindInstructionsInfo);

    const total = totalPremium + (bindInstructionsInfo?.billedFees ?? 0);

    const columnsWidth = isNonEarning
      ? {
          numberOfColumns: 138,
          date: { md: 20, lg: 20, xl: 20 },
          label: { md: 31, lg: 31, xl: 31 },
          columns: { md: 21, lg: 21, xl: 21 },
        }
      : {
          numberOfColumns: 104,
          date: { md: 16, lg: 16, xl: 16 },
          label: { md: 26, lg: 26, xl: 26 },
          columns: { md: 20, lg: 20, xl: 20 },
        };

    return (
      <Row
        {...rowWithNoMarginNorGutter}
        numberOfColumns={columnsWidth.numberOfColumns}
        rowHeight="41px"
      >
        <Col breakpoints={columnsWidth.date} />
        <Col breakpoints={columnsWidth.label} horizontalAlign="flex-start">
          <FontBold>Total of all payments</FontBold>
        </Col>
        {isNonEarning && (
          <>
            <Col
              horizontalMargin="0px"
              breakpoints={columnsWidth.columns}
              horizontalAlign="flex-end"
              verticalAlign="center"
              horizontalGutter="0px"
            >
              <FontBold>
                {currencyFormat(bindInstructionsInfo?.nonEarningAmount ?? 0)}
              </FontBold>
            </Col>
            <Col breakpoints={{ md: 1, lg: 1, xl: 1 }} />
          </>
        )}
        <Col
          horizontalMargin="0px"
          breakpoints={columnsWidth.columns}
          horizontalAlign="flex-end"
          verticalAlign="center"
          horizontalGutter="0px"
        >
          <FontBold>
            {currencyFormat(bindInstructionsInfo?.billedFees, false, 0)}
          </FontBold>
        </Col>
        <Col breakpoints={{ md: 1, lg: 1, xl: 1 }} />
        <Col
          horizontalMargin="0px"
          breakpoints={columnsWidth.columns}
          horizontalAlign="flex-end"
          verticalAlign="center"
          horizontalGutter="0px"
        >
          <FontBold>{currencyFormat(totalPremium, false, 0)}</FontBold>
        </Col>
        <Col breakpoints={{ md: 1, lg: 1, xl: 1 }} />
        <Col
          horizontalMargin="0px"
          breakpoints={columnsWidth.columns}
          horizontalAlign="flex-end"
          verticalAlign="center"
          horizontalGutter="10px"
        >
          <FontBold>{currencyFormat(total, false, 0)}</FontBold>
        </Col>
      </Row>
    );
  };

  const updateInvoicesOnAtomByDueDateChange = (
    updateData: UpdateDueDateInvoiceData
  ) => {
    const invoices = bindInstructionsInfo?.invoices;
    const invoicesUpdated = invoices?.map((invoiceJSON) => {
      if (invoiceJSON.uniqueKey === updateData.uniqueKey) {
        return {
          ...invoiceJSON,
          dueDate: updateData.newDueDate,
          invoiceDate: updateData.newDueDate,
        };
      }
      return invoiceJSON;
    });

    setBindInstructionsInfo({
      ...bindInstructionsInfo,
      invoices: invoicesUpdated,
    });
    setUpdateInvoiceData(null);
  };

  const updateDueDateForInvoiceByRegularTable = (
    options?: ComputeForCellParameters<any>
  ) => {
    const uniqueKey = options?.castedRow?.UniqueKey;
    const newDueDate = GettingDateWithoutTime(options?.currentValue);

    validateInvoicesDueDateOnChange(
      bindInstructionsInfo?.expirationDate,
      options,
      setIsDialogOpen
    );

    setUpdateInvoiceData({ uniqueKey, newDueDate });
  };

  const tableConfiguration: BaseTable2Properties = {
    name: TABLE_NAME,
    tableType: "standard",
    advancedOptions: {
      tableStyle: { height: "fit-content" },
      paginate: false,
      disableOrderBy: true,
      isEditMode: !readOnly,
    },
    columnsAndData: {
      columns: scheduleTableInfo?.scheduleTable?.columns ?? [],
      data: scheduleTableInfo?.scheduleTable?.data ?? [],
    },
    footerOptions: {
      CustomFooterComponent: () =>
        CustomTableFooterComponent(
          scheduleTableInfo?.scheduleTable?.data ?? []
        ),
    },
    ...getCommonTablesProps(readOnly, updateDueDateForInvoiceByRegularTable),
  };

  useBaseTable(tableConfiguration);

  useEffect(() => {
    if (updateInvoiceData !== null) {
      updateInvoicesOnAtomByDueDateChange(updateInvoiceData);
    }
  }, [updateInvoiceData]);

  return <BaseTable2 name={TABLE_NAME} />;
};

export default PolicyQuoteBindInstructionsTable;
